
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

interface RealtimeVoteUpdate {
  postId: string;
  optionCounts: Record<string, number>;
  totalVotes: number;
}

export function usePollVotes() {
  const { user } = useAuth();
  const [votedPolls, setVotedPolls] = useState<Record<string, string[]>>({});
  const [submittedPolls, setSubmittedPolls] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [realtimeVoteCounts, setRealtimeVoteCounts] = useState<Record<string, RealtimeVoteUpdate>>({});
  const channelRef = useRef<any>(null);

  // Load existing votes from database on mount / user change
  useEffect(() => {
    if (!user) {
      setVotedPolls({});
      setSubmittedPolls(new Set());
      return;
    }

    const loadVotes = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('poll_votes')
          .select('post_id, option_ids')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error loading poll votes:', error);
          return;
        }

        if (data && data.length > 0) {
          const votes: Record<string, string[]> = {};
          const submitted = new Set<string>();

          data.forEach((vote) => {
            votes[vote.post_id] = vote.option_ids || [];
            submitted.add(vote.post_id);
          });

          setVotedPolls(votes);
          setSubmittedPolls(submitted);
        }
      } catch (err) {
        console.error('Failed to load poll votes:', err);
      } finally {
        setLoading(false);
      }
    };

    loadVotes();
  }, [user]);

  // Fetch aggregated vote counts for a post from the database
  const fetchVoteCounts = useCallback(async (postId: string) => {
    try {
      const { data, error } = await supabase
        .from('poll_votes')
        .select('option_ids')
        .eq('post_id', postId);

      if (error) {
        console.error('Error fetching vote counts:', error);
        return;
      }

      if (data) {
        const optionCounts: Record<string, number> = {};
        let totalVotes = 0;

        data.forEach((vote) => {
          if (vote.option_ids && Array.isArray(vote.option_ids)) {
            vote.option_ids.forEach((optId: string) => {
              optionCounts[optId] = (optionCounts[optId] || 0) + 1;
            });
            totalVotes += 1;
          }
        });

        setRealtimeVoteCounts(prev => ({
          ...prev,
          [postId]: { postId, optionCounts, totalVotes }
        }));
      }
    } catch (err) {
      console.error('Failed to fetch vote counts:', err);
    }
  }, []);

  // Subscribe to real-time changes on poll_votes table
  useEffect(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const channel = supabase
      .channel('poll-votes-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'poll_votes',
        },
        (payload: any) => {
          const postId = payload.new?.post_id || payload.old?.post_id;
          if (postId) {
            fetchVoteCounts(postId);
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [fetchVoteCounts]);

  // Check if a poll has expired
  const isPollExpired = useCallback((endDate: string | null | undefined): boolean => {
    if (!endDate) return false;
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return new Date() > end;
  }, []);

  // Save vote to database
  const saveVoteToDb = useCallback(async (postId: string, optionIds: string[]) => {
    if (!user) return false;

    try {
      const { data: existing } = await supabase
        .from('poll_votes')
        .select('id')
        .eq('user_id', user.id)
        .eq('post_id', postId)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('poll_votes')
          .update({ option_ids: optionIds })
          .eq('user_id', user.id)
          .eq('post_id', postId);

        if (error) {
          console.error('Error updating poll vote:', error);
          return false;
        }
      } else {
        const { error } = await supabase
          .from('poll_votes')
          .insert({
            user_id: user.id,
            post_id: postId,
            option_ids: optionIds,
          });

        if (error) {
          console.error('Error inserting poll vote:', error);
          return false;
        }
      }

      return true;
    } catch (err) {
      console.error('Failed to save poll vote:', err);
      return false;
    }
  }, [user]);

  // Handle single-choice vote
  const handleSingleVote = useCallback(async (
    postId: string,
    optionId: string,
    endDate?: string | null
  ): Promise<{ success: boolean; message?: string }> => {
    if (!user) {
      return { success: false, message: 'login_required' };
    }

    if (isPollExpired(endDate)) {
      return { success: false, message: 'poll_expired' };
    }

    if (submittedPolls.has(postId)) {
      return { success: false, message: 'already_voted' };
    }

    const saved = await saveVoteToDb(postId, [optionId]);
    if (!saved) {
      return { success: false, message: 'save_failed' };
    }

    setVotedPolls(prev => ({ ...prev, [postId]: [optionId] }));
    setSubmittedPolls(prev => new Set(prev).add(postId));

    return { success: true };
  }, [user, submittedPolls, isPollExpired, saveVoteToDb]);

  // Toggle selection for multi-choice (before submission)
  const toggleMultiOption = useCallback((
    postId: string,
    optionId: string,
    endDate?: string | null
  ): { success: boolean; message?: string } => {
    if (!user) {
      return { success: false, message: 'login_required' };
    }

    if (isPollExpired(endDate)) {
      return { success: false, message: 'poll_expired' };
    }

    if (submittedPolls.has(postId)) {
      return { success: false, message: 'already_voted' };
    }

    const currentVotes = votedPolls[postId] || [];
    if (currentVotes.includes(optionId)) {
      setVotedPolls(prev => ({
        ...prev,
        [postId]: currentVotes.filter(id => id !== optionId),
      }));
    } else {
      setVotedPolls(prev => ({
        ...prev,
        [postId]: [...currentVotes, optionId],
      }));
    }

    return { success: true };
  }, [user, votedPolls, submittedPolls, isPollExpired]);

  // Submit multi-choice vote
  const submitMultiVote = useCallback(async (
    postId: string,
    endDate?: string | null
  ): Promise<{ success: boolean; message?: string }> => {
    if (!user) {
      return { success: false, message: 'login_required' };
    }

    if (isPollExpired(endDate)) {
      return { success: false, message: 'poll_expired' };
    }

    const currentVotes = votedPolls[postId] || [];
    if (currentVotes.length === 0) {
      return { success: false, message: 'no_selection' };
    }

    const saved = await saveVoteToDb(postId, currentVotes);
    if (!saved) {
      return { success: false, message: 'save_failed' };
    }

    setSubmittedPolls(prev => new Set(prev).add(postId));

    return { success: true };
  }, [user, votedPolls, isPollExpired, saveVoteToDb]);

  return {
    votedPolls,
    submittedPolls,
    loading,
    realtimeVoteCounts,
    isPollExpired,
    handleSingleVote,
    toggleMultiOption,
    submitMultiVote,
    fetchVoteCounts,
  };
}
